import React from "react";
import {
  Box,
  TextField,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Typography,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Chip,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { EditingStage, FollowUpGroup } from "../types";
import { newStageAccordionStyles, formTitleStyles } from "../styles";

interface StageEditFormProps {
  editingStage: EditingStage;
  stageError: string;
  onSave: () => void;
  onCancel: () => void;
  onStageNumberChange: (value: number) => void;
  onTitleChange: (value: string) => void;
  onIsPublicChange: (value: boolean) => void;
  onFollowUpGroupIdsChange: (value: string) => void;
  followUpGroups: FollowUpGroup[];
  isNewStage?: boolean;
}

export const StageEditForm: React.FC<StageEditFormProps> = ({
  editingStage,
  stageError,
  onSave,
  onCancel,
  onStageNumberChange,
  onTitleChange,
  onIsPublicChange,
  onFollowUpGroupIdsChange,
  followUpGroups,
  isNewStage = false,
}) => {
  if (!editingStage.sectionId) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={formTitleStyles}>
        {isNewStage ? "افزودن مرحله جدید:" : "ویرایش مرحله:"}
      </Typography>
      <Accordion sx={newStageAccordionStyles} defaultExpanded={isNewStage}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="stage-edit-content"
          id="stage-edit-header"
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <Typography
              variant="body2"
              sx={{ minWidth: 60, fontWeight: "bold" }}
            >
              مرحله {editingStage.stageNumber}:
            </Typography>
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              {editingStage.title || "عنوان مرحله"}
            </Typography>
            {!editingStage.isPublic && (
              <Box
                sx={{
                  px: 1,
                  py: 0.5,
                  bgcolor: "warning.main",
                  color: "white",
                  borderRadius: 1,
                  fontSize: "0.75rem",
                  fontWeight: "bold",
                }}
              >
                خصوصی
              </Box>
            )}
            {!editingStage.isPublic && editingStage.followUpGroupIdsJson && (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {(() => {
                  try {
                    const ids = JSON.parse(editingStage.followUpGroupIdsJson);
                    return ids.map((id: number) => {
                      const group = followUpGroups.find((g) => g.id === id);
                      return group ? (
                        <Box
                          key={id}
                          sx={{
                            px: 0.5,
                            py: 0.25,
                            bgcolor: "primary.main",
                            color: "white",
                            borderRadius: 0.5,
                            fontSize: "0.7rem",
                          }}
                        >
                          {group.title}
                        </Box>
                      ) : null;
                    });
                  } catch {
                    return null;
                  }
                })()}
              </Box>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {stageError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {stageError}
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <TextField
              size="small"
              type="number"
              value={editingStage.stageNumber}
              onChange={(e) =>
                onStageNumberChange(parseInt(e.target.value) || 1)
              }
              sx={{ width: 50 }}
              inputProps={{ min: 1 }}
              label="شماره"
            />
            <TextField
              size="small"
              value={editingStage.title}
              onChange={(e) => onTitleChange(e.target.value)}
              placeholder="عنوان مرحله"
              sx={{ width: 600 }}
              label="عنوان"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editingStage.isPublic}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    onIsPublicChange(checked);
                    if (checked) {
                      // Clear follow-up groups when switching to public
                      onFollowUpGroupIdsChange("");
                    }
                  }}
                />
              }
              label="عمومی"
            />
            {!editingStage.isPublic && (
              <Autocomplete
                multiple
                options={followUpGroups}
                getOptionLabel={(option) => option.title}
                getOptionKey={(option) => option.id}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={followUpGroups.filter((group) => {
                  if (!editingStage.followUpGroupIdsJson) return false;
                  try {
                    const ids = JSON.parse(editingStage.followUpGroupIdsJson);
                    return ids.includes(group.id);
                  } catch {
                    return false;
                  }
                })}
                onChange={(event, newValue) => {
                  const ids = newValue.map((group) => group.id);
                  onFollowUpGroupIdsChange(JSON.stringify(ids));
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.title}
                      {...getTagProps({ index })}
                      key={option.id}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="گروه‌های پیگیری"
                    placeholder="انتخاب..."
                    size="small"
                  />
                )}
                size="small"
                sx={{ minWidth: 200, flexGrow: 1 }}
              />
            )}
          </Box>

          <Box
            sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end" }}
          >
            <IconButton
              onClick={onSave}
              size="small"
              color="primary"
              title="ذخیره"
            >
              <SaveIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={onCancel}
              size="small"
              color="error"
              title="انصراف"
            >
              <CancelIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {isNewStage
              ? "مرحله جدید در حال ایجاد..."
              : "مرحله در حال ویرایش..."}
          </Typography>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
